import React, { useEffect, useState } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  createGoalCycleConfig,
  updateGoalCycleConfig,
} from "../../services/goalService";
import { getDepartmentById } from "../../services/departmentService";
import { companyID } from "../../auth";

const GoalCycleModal = ({ show, handleClose, editData, fetchConfiguration }) => {
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    cycle_name: "",
    cycle_start_date: "",
    cycle_end_date: "",
    goal_creation_deadline: "",
    self_review_deadline: "",
    appraisal_deadline: "",
    department_id: "",
    default: 1,
  });

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const company_id = companyID();
  const emp_id = localStorage.getItem("employeeID");

  useEffect(() => {
    if (show) {
      fetchDepartments();
    }
  }, [show]);

  useEffect(() => {
    if (editData) {
      const formatDate = (dateValue) => {
        if (!dateValue) return "";
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "";
        return date.toISOString().split('T')[0];
      };

      setFormData({
        year: editData.year || new Date().getFullYear(),
        cycle_name: editData.cycle_name || "",
        cycle_start_date: formatDate(editData.cycle_start_date),
        cycle_end_date: formatDate(editData.cycle_end_date),
        goal_creation_deadline: formatDate(editData.goal_creation_deadline),
        self_review_deadline: formatDate(editData.self_review_deadline),
        appraisal_deadline: formatDate(editData.appraisal_deadline),
        department_id: editData.department_ids
          ? editData.department_ids.split(",")[0]
          : "",
        default: editData.default || 1,
      });
    } else {
      resetForm();
    }
  }, [editData]);

  const fetchDepartments = async () => {
    try {
      const response = await getDepartmentById(company_id);
      if (response?.result) {
        setDepartments(response.result);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error("Failed to fetch departments");
    }
  };

  const resetForm = () => {
    setFormData({
      year: new Date().getFullYear(),
      cycle_name: "",
      cycle_start_date: "",
      cycle_end_date: "",
      goal_creation_deadline: "",
      self_review_deadline: "",
      appraisal_deadline: "",
      department_id: "",
      default: 1,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDepartmentChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      department_id: e.target.value,
    }));
  };

  const validateForm = () => {
    if (!formData.cycle_name.trim()) {
      toast.error("Cycle name is required");
      return false;
    }
    if (!formData.cycle_start_date) {
      toast.error("Cycle start date is required");
      return false;
    }
    if (!formData.cycle_end_date) {
      toast.error("Cycle end date is required");
      return false;
    }
    if (new Date(formData.cycle_start_date) >= new Date(formData.cycle_end_date)) {
      toast.error("Cycle end date must be after start date");
      return false;
    }
    if (!formData.department_id) {
      toast.error("Please select a department");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        department_ids: [parseInt(formData.department_id)],
        goal_creation_deadline: formData.goal_creation_deadline || null,
        self_review_deadline: formData.self_review_deadline || null,
        appraisal_deadline: formData.appraisal_deadline || null,
        goal_creation_status: formData.goal_creation_deadline ? 1 : 0,
        self_review_status: formData.self_review_deadline ? 1 : 0,
        eligibility_date: formData.cycle_start_date,
        appraisal_effective_month: formData.appraisal_deadline,
        company_id: company_id,
        created_by: emp_id,
        updated_by: emp_id,
      };
      delete payload.department_id;

      if (editData) {
        await updateGoalCycleConfig(editData.id, payload);
        toast.success("Goal cycle updated successfully");
      } else {
        await createGoalCycleConfig(payload);
        toast.success("Goal cycle created successfully");
      }

      fetchConfiguration();
      handleClose();
      resetForm();
    } catch (error) {
      console.error("Error saving goal cycle:", error);
      toast.error(
        error.response?.data?.message || "Failed to save goal cycle"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {editData ? "Edit Goal Cycle" : "Create Goal Cycle"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Year <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  placeholder="Enter year"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Cycle Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="cycle_name"
                  value={formData.cycle_name}
                  onChange={handleInputChange}
                  placeholder="Enter cycle name"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Cycle Start Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="cycle_start_date"
                  value={formData.cycle_start_date}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Cycle End Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="cycle_end_date"
                  value={formData.cycle_end_date}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Goal Creation Deadline <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="goal_creation_deadline"
                  value={formData.goal_creation_deadline}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Performance Review Deadline <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="self_review_deadline"
                  value={formData.self_review_deadline}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Appraisal Deadline <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="appraisal_deadline"
                  value={formData.appraisal_deadline}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Department <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleDepartmentChange}
                  required
                >
                  <option value="">Select department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.department_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Saving..." : editData ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default GoalCycleModal;
